(() => {
  if (window.CartGiftController || !window.CartGiftEligibility) return;

  const DISCOUNTS = [20, 25, 30];
  const OPENING_DURATION = 3200;
  const SUCCESS_DURATION = 9000;
  const FINAL_SUCCESS_DURATION = 5000;
  const VALIDATED_CART_MAX_AGE = 30000;
  const state = {
    inspectScheduled: false,
    isSelectingGift: false,
    itemsCount: null,
    lastFocusedElement: null,
    modal: null,
    observer: null,
    pendingTier: null,
    previewTier: null,
    progressTimer: null,
    reconcilePromise: null,
    reconcileTimer: null,
    revealTimer: null,
    successTimer: null,
    suppressNextOpen: false,
    validatedCart: null,
    validatedCartAt: 0,
    validatedPicker: null,
  };
  window.CartGiftController = state;

  const getPicker = () => document.querySelector('cart-drawer [data-cart-gift-picker]');
  const getItemsCount = (picker) => Number.parseInt(picker?.dataset.itemsCount || '0', 10);
  const getThresholds = (picker) =>
    String(picker?.dataset.thresholds || '')
      .split(',')
      .map((value) => Number.parseInt(value, 10))
      .filter(Number.isFinite);
  const getHighestUnlockedTier = (thresholds, paidCount, fallbackTier = 1) =>
    thresholds.reduce(
      (current, threshold, index) => paidCount >= threshold ? index + 1 : current,
      fallbackTier
    );
  const getTemplate = (picker, tier) =>
    picker?.querySelector(`[data-gift-modal-template][data-gift-tier="${tier}"]`) || null;
  const isRewardGift = (item) => String(item?.properties?._reward_gift || '') === 'true';

  const toFamily = (value) =>
    String(value || '')
      .split('|')[0]
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const getItemFamily = (item) =>
    toFamily(item?.properties?._reward_family || item?.product_title || item?.title);

  const getRewardTier = (item, tiersCount = DISCOUNTS.length) => {
    const tier = Number.parseInt(item?.properties?._reward_tier || '', 10);
    return Number.isInteger(tier) && tier >= 1 && tier <= tiersCount ? tier : null;
  };

  const getPaidItemsCount = (cart) => window.CartGiftEligibility.getPaidItemsCount(cart);

  const clearValidatedCart = () => {
    state.validatedCart = null;
    state.validatedCartAt = 0;
    state.validatedPicker = null;
  };

  const rememberValidatedCart = (cart, picker) => {
    if (!cart || !picker) return;
    state.validatedCart = cart;
    state.validatedCartAt = Date.now();
    state.validatedPicker = picker;
  };

  const getValidatedCart = (picker) => {
    const isCurrent =
      state.validatedCart &&
      state.validatedPicker === picker &&
      Date.now() - state.validatedCartAt <= VALIDATED_CART_MAX_AGE &&
      getPaidItemsCount(state.validatedCart) === getItemsCount(picker);
    if (!isCurrent) {
      clearValidatedCart();
      return null;
    }
    return state.validatedCart;
  };

  const getJsonUrl = (url) => (url.endsWith('.js') ? url : `${url}.js`);
  const getSectionsPayload = () => ({
    sections: ['cart-drawer', 'cart-icon-bubble'],
    sections_url: window.location.pathname,
  });

  const parseResponse = async (response, fallbackMessage) => {
    const result = await response.json();
    if (!response.ok || result?.status) {
      throw new Error(result?.description || result?.message || fallbackMessage);
    }
    return result;
  };

  const fetchCart = async () => {
    const response = await fetch(getJsonUrl(window.routes?.cart_url || '/cart'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    return parseResponse(response, 'The cart could not be loaded.');
  };

  const requestCartUpdate = async (updates) => {
    const response = await fetch(getJsonUrl(window.routes?.cart_update_url || '/cart/update'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates, ...getSectionsPayload() }),
    });
    return parseResponse(response, 'The cart could not be updated.');
  };

  const requestCartAdd = async (items) => {
    const response = await fetch(getJsonUrl(window.routes?.cart_add_url || '/cart/add'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items, ...getSectionsPayload() }),
    });
    return parseResponse(response, 'The gift could not be added.');
  };

  const renderCartResult = (result) => {
    clearValidatedCart();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && result?.sections) {
      drawer.renderContents(result, true);
      return;
    }
    window.location.reload();
  };

  const setChoicesAvailable = (picker, hasChoices) => {
    if (!picker) return;
    picker.dataset.hasGiftChoices = hasChoices ? 'true' : 'false';
    const itemsCount = getItemsCount(picker);
    const thresholds = getThresholds(picker);
    picker.querySelectorAll('[data-gift-trigger]').forEach((trigger) => {
      const tier = Number.parseInt(trigger.dataset.tier || '', 10);
      const unlocked = Number.isInteger(tier) && itemsCount >= thresholds[tier - 1];
      trigger.disabled =
        trigger.dataset.hasReward === 'true' || !hasChoices || !unlocked;
    });
  };

  const applyProgressState = (picker) => {
    if (!picker) return;
    const itemsCount = getItemsCount(picker);
    const thresholds = getThresholds(picker);
    picker.querySelectorAll('[data-gift-trigger]').forEach((trigger) => {
      const tier = Number.parseInt(trigger.dataset.tier || '', 10);
      const unlocked = Number.isInteger(tier) && itemsCount >= thresholds[tier - 1];
      const hasReward = trigger.dataset.hasReward === 'true';
      const previewOpen = state.previewTier === tier && state.modal !== null;
      trigger.disabled =
        hasReward || !unlocked || picker.dataset.hasGiftChoices !== 'true';
      if (!unlocked) {
        trigger.classList.remove('is-opening', 'is-opened');
        return;
      }
      if (!previewOpen) trigger.classList.remove('is-opening');
      if (!trigger.classList.contains('is-opening')) {
        trigger.classList.toggle('is-opened', hasReward || previewOpen);
      }
    });
  };

  const startProgressOpening = (picker, tier) => {
    const trigger = picker?.querySelector(`[data-gift-trigger][data-tier="${tier}"]`);
    if (!trigger || trigger.dataset.hasReward === 'true') return;
    window.clearTimeout(state.progressTimer);
    state.previewTier = tier;
    trigger.classList.remove('is-opened');
    trigger.classList.add('is-opening');
    const icon = trigger.querySelector('.cart-progress__gift-icon--opening');
    if (icon) icon.replaceWith(icon.cloneNode(true));
    state.progressTimer = window.setTimeout(() => {
      const current = getPicker()?.querySelector(`[data-gift-trigger][data-tier="${tier}"]`);
      if (state.modal && state.previewTier === tier) {
        current?.classList.remove('is-opening');
        current?.classList.add('is-opened');
      }
    }, 2550);
  };

  const buildReconciliationUpdates = (cart, picker) => {
    const thresholds = getThresholds(picker);
    const paidCount = getPaidItemsCount(cart);
    const occupiedTiers = new Set();
    const occupiedFamilies = new Set();
    const updates = {};

    (cart?.items || []).filter(isRewardGift).forEach((item) => {
      const tier = getRewardTier(item, thresholds.length);
      const family = getItemFamily(item);
      const quantity = Math.max(1, Number(item?.quantity) || 1);
      const invalid =
        !tier ||
        paidCount < thresholds[tier - 1] ||
        !family ||
        occupiedTiers.has(tier) ||
        occupiedFamilies.has(family);
      if (invalid) {
        updates[item.key] = 0;
        return;
      }
      occupiedTiers.add(tier);
      occupiedFamilies.add(family);
      if (quantity > 1) updates[item.key] = 1;
    });
    return updates;
  };

  const performReconciliation = async (picker) => {
    const cart = await fetchCart();
    const updates = buildReconciliationUpdates(cart, picker);
    if (!Object.keys(updates).length) return { cart, changed: false };
    state.suppressNextOpen = true;
    try {
      const result = await requestCartUpdate(updates);
      renderCartResult(result);
      return { cart: null, changed: true };
    } catch (error) {
      state.suppressNextOpen = false;
      throw error;
    }
  };

  const reconcileRewards = (picker = getPicker()) => {
    if (!picker) return Promise.resolve({ cart: null, changed: false });
    if (state.reconcilePromise) return state.reconcilePromise;
    const task = performReconciliation(picker).finally(() => {
      if (state.reconcilePromise === task) state.reconcilePromise = null;
    });
    state.reconcilePromise = task;
    return task;
  };

  const scheduleReconciliation = () => {
    if (state.isSelectingGift) return;
    window.clearTimeout(state.reconcileTimer);
    state.reconcileTimer = window.setTimeout(() => {
      reconcileRewards().catch((error) =>
        console.warn('Gift reward reconciliation failed:', error)
      );
    }, 120);
  };

  const ensureRecommendations = async (picker, tier, retry = true) => {
    const template = getTemplate(picker, tier);
    if (!template) return false;
    template.dataset.recommendationState = 'loading';
    try {
      const cart = await fetchCart();
      if (getPaidItemsCount(cart) === 0) {
        setChoicesAvailable(picker, false);
        return false;
      }
      const engine = window.CartGiftRecommendations;
      const renderer = window.CartGiftRenderer;
      if (!engine || !renderer) throw new Error('The gift recommendation modules are unavailable.');
      const result = await engine.getRecommendations({ cart, picker, tier });
      const currentCart = await fetchCart();
      const currentPicker = getPicker() || picker;
      const currentFingerprint = engine.getFingerprint(
        currentCart,
        currentPicker.dataset.giftAudience || 'default'
      );
      if (currentFingerprint !== result.fingerprint) {
        if (retry) return ensureRecommendations(currentPicker, tier, false);
        throw new Error('The cart changed while gift recommendations were loading.');
      }
      rememberValidatedCart(currentCart, currentPicker);
      const rendered = renderer.render({
        picker: currentPicker,
        products: result.products,
        source: result.source,
        tier,
      });
      const fallbackAvailable = renderer.hasFallbackChoices(currentPicker, tier);
      const available = rendered || fallbackAvailable;
      setChoicesAvailable(currentPicker, available);
      const currentTemplate = getTemplate(currentPicker, tier);
      if (currentTemplate) {
        currentTemplate.dataset.recommendationState = rendered ? 'ready' : 'fallback';
      }
      return available;
    } catch (error) {
      const currentPicker = getPicker() || picker;
      const available = Boolean(
        window.CartGiftRenderer?.hasFallbackChoices(currentPicker, tier)
      );
      setChoicesAvailable(currentPicker, available);
      const currentTemplate = getTemplate(currentPicker, tier);
      if (currentTemplate) currentTemplate.dataset.recommendationState = 'fallback';
      console.warn('Gift recommendations used the managed fallback:', error);
      return available;
    }
  };

  const clearTimers = () => {
    window.clearTimeout(state.progressTimer);
    window.clearTimeout(state.revealTimer);
    window.clearTimeout(state.successTimer);
    state.progressTimer = null;
    state.revealTimer = null;
    state.successTimer = null;
  };

  const prepareRewardArtwork = (modal, tier) => {
    const artwork = modal?.querySelector('.cart-gift-picker__success-artwork');
    if (!artwork) return null;
    const index = Math.max(0, tier - 1);
    const source = String(artwork.dataset.rewardSources || '').split('|')[index];
    const sourceSet = String(artwork.dataset.rewardSrcsets || '').split('|')[index];
    const dimensions = String(artwork.dataset.rewardDimensions || '').split('|')[index];
    const match = /^(\d+)x(\d+)$/.exec(dimensions || '');
    if (sourceSet) artwork.srcset = sourceSet;
    if (source) artwork.src = source;
    if (match) {
      artwork.width = Number.parseInt(match[1], 10);
      artwork.height = Number.parseInt(match[2], 10);
    }
    return artwork;
  };

  const closeModal = (restoreFocus = true) => {
    if (!state.modal) return;
    clearTimers();
    state.modal.remove();
    state.modal = null;
    state.previewTier = null;
    document.documentElement.classList.remove('cart-gift-modal-open');
    applyProgressState(getPicker());
    if (
      restoreFocus &&
      state.lastFocusedElement instanceof HTMLElement &&
      state.lastFocusedElement.isConnected
    ) {
      state.lastFocusedElement.focus();
    }
  };

  const disableSelectedChoices = async (modal, tier) => {
    try {
      const picker = getPicker();
      let cart = getValidatedCart(picker);
      if (!cart) {
        cart = await fetchCart();
        if (state.modal !== modal || getPicker() !== picker) return;
        rememberValidatedCart(cart, picker);
      }
      if (state.modal !== modal) return;
      const selectedFamilies = new Map();
      (cart?.items || []).filter(isRewardGift).forEach((item) => {
        const family = getItemFamily(item);
        if (family) selectedFamilies.set(family, getRewardTier(item));
      });
      modal.querySelectorAll('[data-gift-choice]').forEach((choice) => {
        const selectedTier = selectedFamilies.get(choice.dataset.productFamily || '');
        if (!selectedTier) return;
        choice.classList.add('is-current-reward');
        choice.setAttribute('aria-disabled', 'true');
        choice.setAttribute(
          'aria-label',
          selectedTier === tier
            ? 'This gift is already selected for this reward'
            : 'This product is already selected as another reward'
        );
      });
    } catch (error) {
      console.warn('Selected gift choices could not be inspected:', error);
    }
  };

  const openModal = (picker, tier) => {
    const template = getTemplate(picker, tier);
    if (!template) return false;
    const fragment = template.content.cloneNode(true);
    const modal = fragment.querySelector('[data-gift-modal]');
    const animationTemplate = fragment.querySelector('[data-gift-animation-template]');
    const animationHost = fragment.querySelector('[data-gift-animation]');
    const closeButton = fragment.querySelector('[data-gift-close]');
    if (!modal || !animationTemplate || !animationHost || !closeButton) return false;

    const successTier = getHighestUnlockedTier(getThresholds(picker), getItemsCount(picker), tier);
    prepareRewardArtwork(modal, successTier);

    closeModal(false);
    state.lastFocusedElement = document.activeElement;
    animationHost.replaceChildren(animationTemplate.content.cloneNode(true));
    animationTemplate.remove();
    animationHost.inert = true;
    closeButton.hidden = true;
    closeButton.disabled = true;

    const reveal = () => {
      if (state.modal !== modal || modal.dataset.phase !== 'opening') return;
      window.clearTimeout(state.revealTimer);
      animationHost.inert = false;
      modal.dataset.phase = 'choices';
      closeButton.hidden = false;
      closeButton.disabled = false;
      closeButton.focus();
    };
    animationHost
      .querySelector('[data-gift-animation-end]')
      ?.addEventListener('endEvent', reveal, { once: true });

    modal.hidden = false;
    modal.dataset.activeTier = String(tier);
    document.body.appendChild(fragment);
    state.modal = modal;
    startProgressOpening(picker, tier);
    document.documentElement.classList.add('cart-gift-modal-open');
    modal.classList.remove('is-opening');
    void modal.offsetWidth;
    modal.classList.add('is-opening');
    state.revealTimer = window.setTimeout(reveal, OPENING_DURATION);
    disableSelectedChoices(modal, tier);
    return true;
  };

  const openPreparedModal = async (picker, tier) => {
    if (!picker || state.pendingTier !== null) return false;
    const trigger = picker.querySelector(`[data-gift-trigger][data-tier="${tier}"]`);
    if (!trigger || trigger.disabled || trigger.dataset.hasReward === 'true') return false;
    state.pendingTier = tier;
    picker.setAttribute('aria-busy', 'true');
    try {
      await reconcileRewards(picker);
      const currentPicker = getPicker() || picker;
      const available = await ensureRecommendations(currentPicker, tier);
      const finalPicker = getPicker() || currentPicker;
      const thresholds = getThresholds(finalPicker);
      const currentTrigger = finalPicker.querySelector(
        `[data-gift-trigger][data-tier="${tier}"]`
      );
      if (
        !available ||
        !currentTrigger ||
        currentTrigger.dataset.hasReward === 'true' ||
        getItemsCount(finalPicker) < thresholds[tier - 1]
      ) return false;
      return openModal(finalPicker, tier);
    } finally {
      picker.removeAttribute('aria-busy');
      state.pendingTier = null;
    }
  };

  const inspectCart = () => {
    const picker = getPicker();
    if (!picker) return;
    if (state.validatedPicker && state.validatedPicker !== picker) clearValidatedCart();
    applyProgressState(picker);
    scheduleReconciliation();
    const currentCount = getItemsCount(picker);
    if (!Number.isFinite(state.itemsCount)) {
      state.itemsCount = currentCount;
      return;
    }
    const previousCount = state.itemsCount;
    state.itemsCount = currentCount;
    if (previousCount !== currentCount) clearValidatedCart();
    if (state.suppressNextOpen) {
      state.suppressNextOpen = false;
      return;
    }
    const thresholds = getThresholds(picker);
    if (!thresholds.some((threshold) => previousCount < threshold && currentCount >= threshold)) {
      return;
    }
    const tier = thresholds.filter((threshold) => threshold <= currentCount).length;
    openPreparedModal(picker, Math.max(1, tier));
  };

  const scheduleInspection = () => {
    if (state.inspectScheduled) return;
    state.inspectScheduled = true;
    window.requestAnimationFrame(() => {
      state.inspectScheduled = false;
      inspectCart();
    });
  };

  const showStatus = (message, isError = false) => {
    const status = state.modal?.querySelector('[data-gift-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  };

  const getVariantOptions = (variant) => {
    try {
      return JSON.parse(variant.dataset.options || '[]');
    } catch (error) {
      return [];
    }
  };

  const closeCustomSelect = (customSelect, restoreFocus = false) => {
    const trigger = customSelect?.querySelector('[data-gift-select-trigger]');
    const options = customSelect?.querySelector('[data-gift-select-options]');
    if (!trigger || !options) return;
    trigger.setAttribute('aria-expanded', 'false');
    options.hidden = true;
    if (restoreFocus) trigger.focus();
  };

  const closeCustomSelects = (except = null) => {
    state.modal?.querySelectorAll('[data-gift-custom-select]').forEach((select) => {
      if (select !== except) closeCustomSelect(select);
    });
  };

  const openCustomSelect = (customSelect, focusLast = false) => {
    const trigger = customSelect?.querySelector('[data-gift-select-trigger]');
    const options = customSelect?.querySelector('[data-gift-select-options]');
    if (!trigger || !options) return;
    closeCustomSelects(customSelect);
    trigger.setAttribute('aria-expanded', 'true');
    options.hidden = false;
    const enabled = [...options.querySelectorAll('[data-gift-select-option]:not(:disabled)')];
    const selected = options.querySelector('[data-gift-select-option][aria-selected="true"]');
    (focusLast ? enabled[enabled.length - 1] : selected || enabled[0])?.focus();
  };

  const chooseCustomOption = (option) => {
    const customSelect = option?.closest('[data-gift-custom-select]');
    const nativeSelect = customSelect?.querySelector('select[data-gift-option]');
    if (!customSelect || !nativeSelect || option.disabled) return;
    nativeSelect.value = option.dataset.value || '';
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    closeCustomSelect(customSelect, true);
  };

  const syncVariant = (panel, announce = false) => {
    if (!panel) return null;
    const groups = [...panel.querySelectorAll('[data-gift-option-group]')].sort(
      (first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition)
    );
    const variants = [...panel.querySelectorAll('[data-gift-variant]')];
    const available = variants.filter((variant) => variant.dataset.available === 'true');
    const selectedOptions = [];
    groups.forEach((group, optionIndex) => {
      const allowed = new Set();
      available.forEach((variant) => {
        const options = getVariantOptions(variant);
        const previousMatch = selectedOptions.every(
          (value, index) => value && options[index] === value
        );
        if (previousMatch && options[optionIndex]) allowed.add(options[optionIndex]);
      });
      const select = group.querySelector('select[data-gift-option]');
      if (!select) return;
      [...select.options].forEach((option) => {
        option.hidden = !allowed.has(option.value);
        option.disabled = !allowed.has(option.value);
      });
      let selected = select.value;
      if (!allowed.has(selected)) {
        selected = [...select.options].find((option) => allowed.has(option.value))?.value || '';
        select.value = selected;
      }
      selectedOptions.push(selected);
      const label = group.querySelector('[data-gift-option-selected]');
      const value = group.querySelector('[data-gift-select-value]');
      if (label) label.textContent = selected;
      if (value) value.textContent = selected;
      group.querySelectorAll('[data-gift-select-option]').forEach((option) => {
        const enabled = allowed.has(option.dataset.value || '');
        option.disabled = !enabled;
        option.hidden = !enabled;
        option.setAttribute('aria-selected', option.dataset.value === selected ? 'true' : 'false');
      });
    });
    const matched = available.find((variant) => {
      const options = getVariantOptions(variant);
      return groups.length === 0 || selectedOptions.every((value, index) => options[index] === value);
    });
    variants.forEach((variant) => { variant.checked = variant === matched; });
    const confirm = panel.querySelector('[data-gift-confirm]');
    if (confirm) confirm.disabled = !matched;
    if (announce) showStatus(matched ? '' : 'This combination is unavailable.', !matched);
    return matched || null;
  };

  const showVariantPicker = (choice) => {
    const modal = state.modal;
    if (!modal || modal.dataset.phase !== 'choices') return;
    const productIndex = choice.dataset.productIndex;
    const panel = modal.querySelector(`[data-gift-variant-panel="${productIndex}"]`);
    const animation = modal.querySelector('[data-gift-animation]');
    if (!panel || !animation) return;
    modal.dataset.phase = 'variant';
    modal.dataset.activeProductIndex = productIndex;
    animation.classList.add('has-active-variant');
    panel.hidden = false;
    syncVariant(panel);
    showStatus('');
    panel.querySelector('[data-gift-select-trigger], [data-gift-confirm]:not(:disabled)')?.focus();
  };

  const returnToChoices = () => {
    const modal = state.modal;
    if (!modal || modal.dataset.phase !== 'variant') return;
    const productIndex = modal.dataset.activeProductIndex;
    modal.querySelector(`[data-gift-variant-panel="${productIndex}"]`)?.setAttribute('hidden', '');
    modal.querySelector('[data-gift-animation]')?.classList.remove('has-active-variant');
    modal.dataset.phase = 'choices';
    showStatus('');
    modal.querySelector(`[data-gift-choice][data-product-index="${productIndex}"]`)?.focus();
  };

  const showSuccess = async (tier, thresholds, paidCount) => {
    const modal = state.modal;
    if (!modal) return;
    showStatus('');
    const highestTier = getHighestUnlockedTier(thresholds, paidCount, tier);
    const discount = DISCOUNTS[highestTier - 1];
    const animation = modal.querySelector('[data-gift-animation]');
    const success = modal.querySelector('[data-gift-success]');
    const artwork = modal.querySelector('.cart-gift-picker__success-artwork');
    const nextCopy = modal.querySelector('[data-gift-success-next-copy]');
    const nextReward = modal.querySelector('[data-gift-success-next-reward]');
    const cta = modal.querySelector('[data-gift-success-cta]');
    const ctaLabel = modal.querySelector('[data-gift-success-cta-label]');
    const close = modal.querySelector('[data-gift-close]');
    if (!animation || !success || !artwork || !nextCopy || !nextReward || !cta || !ctaLabel || !close) return;
    prepareRewardArtwork(modal, highestTier);
    artwork.alt = `${highestTier} free gifts and ${discount}% off reward`;
    try {
      if (typeof artwork.decode === 'function') await artwork.decode();
    } catch (error) {
      console.warn('Reward artwork could not be decoded before display:', error);
    }
    if (state.modal !== modal) return;
    if (highestTier < thresholds.length) {
      const remaining = Math.max(0, thresholds[highestTier] - paidCount);
      nextCopy.textContent = `Add ${remaining} more ${remaining === 1 ? 'jewelry piece' : 'jewelry pieces'} to unlock your next reward:`;
      nextReward.textContent = `${highestTier + 1} FREE GIFTS + ${DISCOUNTS[highestTier]}% OFF.`;
      ctaLabel.textContent = 'Unlock the next gifts';
    } else {
      nextCopy.textContent = '';
      nextReward.textContent = '';
      nextCopy.parentElement.hidden = true;
      ctaLabel.textContent = `Enjoy your ${discount}% OFF`;
    }
    animation.hidden = true;
    success.hidden = false;
    close.hidden = false;
    close.disabled = false;
    modal.dataset.phase = 'success';
    success.tabIndex = -1;
    success.focus({ preventScroll: true });
    state.successTimer = window.setTimeout(
      () => state.modal === modal && closeModal(),
      highestTier === thresholds.length ? FINAL_SUCCESS_DURATION : SUCCESS_DURATION
    );
  };

  const addSelectedVariant = async (panel, errorPhase = 'variant') => {
    const variant = panel?.querySelector('[data-gift-variant]:checked');
    const variantId = Number.parseInt(variant?.value || '', 10);
    const family = toFamily(panel?.dataset.productFamily);
    const tier = Number.parseInt(state.modal?.dataset.activeTier || '', 10);
    const picker = getPicker();
    const thresholds = getThresholds(picker);
    let paidCount = getItemsCount(picker);
    if (!variantId || !family) {
      showStatus('Choose an available gift variant first.', true);
      return;
    }
    if (!tier || paidCount < thresholds[tier - 1]) {
      showStatus('This gift is not unlocked yet.', true);
      return;
    }

    const modal = state.modal;
    const controls = [...panel.querySelectorAll('input, select, button')];
    const disabledState = new Map(controls.map((control) => [control, control.disabled]));
    controls.forEach((control) => { control.disabled = true; });
    modal.dataset.phase = 'adding';
    state.isSelectingGift = true;
    state.suppressNextOpen = true;
    showStatus('Adding your selected gift to the cart…');
    try {
      const reconciliation = await reconcileRewards(picker);
      const cart = reconciliation.cart || await fetchCart();
      paidCount = getPaidItemsCount(cart);
      if (paidCount < thresholds[tier - 1]) {
        throw new Error('This gift is not unlocked yet.');
      }
      if (getPicker() === picker) rememberValidatedCart(cart, picker);
      const familyUsed = (cart?.items || []).some(
        (item) => isRewardGift(item) && getItemFamily(item) === family
      );
      const tierUsed = (cart?.items || []).some(
        (item) => isRewardGift(item) && getRewardTier(item) === tier
      );
      if (familyUsed || tierUsed) throw new Error('Choose a different gift for this reward.');
      const result = await requestCartAdd([{
        id: variantId,
        quantity: 1,
        properties: {
          _reward_gift: 'true',
          _reward_tier: String(tier),
          _reward_family: family,
        },
      }]);
      renderCartResult(result);
      await showSuccess(tier, thresholds, paidCount);
    } catch (error) {
      state.suppressNextOpen = false;
      modal.dataset.phase = errorPhase;
      controls.forEach((control) => { control.disabled = disabledState.get(control) || false; });
      syncVariant(panel);
      showStatus(error.message || 'The gift could not be added.', true);
    } finally {
      state.isSelectingGift = false;
    }
  };

  const selectChoice = (choice) => {
    const modal = state.modal;
    if (
      !modal ||
      modal.dataset.phase !== 'choices' ||
      choice?.getAttribute('aria-disabled') === 'true'
    ) return;
    const productIndex = choice.dataset.productIndex;
    const panel = modal.querySelector(`[data-gift-variant-panel="${productIndex}"]`);
    if (!panel) return;
    const variants = [...panel.querySelectorAll('[data-gift-variant]')].filter(
      (variant) => variant.dataset.available === 'true'
    );
    if (variants.length !== 1) {
      showVariantPicker(choice);
      return;
    }
    variants[0].checked = true;
    modal.dataset.activeProductIndex = productIndex;
    addSelectedVariant(panel, 'choices');
  };

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-gift-option]');
    if (option && state.modal?.contains(option)) {
      syncVariant(option.closest('[data-gift-variant-panel]'), true);
    }
  });

  document.addEventListener('click', (event) => {
    const selectOption = event.target.closest('[data-gift-select-option]');
    if (selectOption && state.modal?.contains(selectOption) && !selectOption.disabled) {
      chooseCustomOption(selectOption);
      return;
    }
    const selectTrigger = event.target.closest('[data-gift-select-trigger]');
    if (selectTrigger && state.modal?.contains(selectTrigger) && !selectTrigger.disabled) {
      const customSelect = selectTrigger.closest('[data-gift-custom-select]');
      if (selectTrigger.getAttribute('aria-expanded') === 'true') closeCustomSelect(customSelect, true);
      else openCustomSelect(customSelect);
      return;
    }
    if (state.modal && !event.target.closest('[data-gift-custom-select]')) closeCustomSelects();

    const close = event.target.closest('[data-gift-close]');
    if (close && state.modal?.contains(close) && !close.disabled) return closeModal();
    const success = event.target.closest('[data-gift-success-cta]');
    if (success && state.modal?.contains(success)) return closeModal();
    const backdrop = event.target.closest('.cart-gift-picker__backdrop');
    if (backdrop && state.modal?.contains(backdrop) && !['opening', 'adding'].includes(state.modal.dataset.phase)) {
      return closeModal();
    }
    const back = event.target.closest('[data-gift-back]');
    if (back && state.modal?.contains(back)) return returnToChoices();
    const confirm = event.target.closest('[data-gift-confirm]');
    if (confirm && state.modal?.contains(confirm) && !confirm.disabled) {
      return addSelectedVariant(confirm.closest('[data-gift-variant-panel]'));
    }
    const choice = event.target.closest('[data-gift-choice]');
    if (choice && state.modal?.contains(choice) && choice.getAttribute('aria-disabled') !== 'true') {
      return selectChoice(choice);
    }
    const trigger = event.target.closest('[data-gift-trigger]');
    const picker = trigger?.closest('[data-cart-gift-picker]');
    if (trigger && picker && !trigger.disabled) {
      openPreparedModal(picker, Number.parseInt(trigger.dataset.tier || '1', 10));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!state.modal) return;
    const openSelect = state.modal.querySelector(
      '[data-gift-select-trigger][aria-expanded="true"]'
    )?.closest('[data-gift-custom-select]');
    if (event.key === 'Escape' && openSelect) {
      event.preventDefault();
      closeCustomSelect(openSelect, true);
      return;
    }
    if (event.key === 'Escape' && !['opening', 'adding'].includes(state.modal.dataset.phase)) {
      event.preventDefault();
      closeModal();
      return;
    }
    const choice = event.target.closest('[data-gift-choice]');
    if (
      choice &&
      choice.getAttribute('aria-disabled') !== 'true' &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      selectChoice(choice);
      return;
    }
    const trigger = event.target.closest('[data-gift-select-trigger]');
    if (trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      openCustomSelect(trigger.closest('[data-gift-custom-select]'), event.key === 'ArrowUp');
      return;
    }
    const option = event.target.closest('[data-gift-select-option]');
    if (option) {
      const customSelect = option.closest('[data-gift-custom-select]');
      const enabled = [...customSelect.querySelectorAll('[data-gift-select-option]:not(:disabled)')];
      const index = enabled.indexOf(option);
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        chooseCustomOption(option);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const offset = event.key === 'ArrowDown' ? 1 : -1;
        enabled[(index + offset + enabled.length) % enabled.length]?.focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        enabled[event.key === 'Home' ? 0 : enabled.length - 1]?.focus();
      } else if (event.key === 'Tab') closeCustomSelect(customSelect);
      return;
    }
    if (event.key === 'Tab') {
      const focusable = [...state.modal.querySelectorAll(
        'button:not([disabled]):not([hidden]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.closest('[hidden]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  const refreshRestoredCart = async () => {
    clearValidatedCart();
    closeModal(false);
    state.itemsCount = null;
    const reconciliation = await reconcileRewards();
    if (reconciliation.changed || !getPicker()) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', getSectionsPayload().sections.join(','));
    const response = await fetch(url.href, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const sections = await parseResponse(response, 'The cart could not be refreshed.');
    if (typeof sections?.['cart-drawer'] !== 'string') {
      throw new Error('The refreshed cart drawer is unavailable.');
    }
    renderCartResult({ sections });
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      refreshRestoredCart().catch((error) =>
        console.warn('The restored gift cart could not be refreshed:', error)
      );
    }
  });

  const init = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const picker = getPicker();
    state.itemsCount = getItemsCount(picker);
    applyProgressState(picker);
    state.observer = new MutationObserver(scheduleInspection);
    state.observer.observe(drawer, { childList: true, subtree: true });
    scheduleReconciliation();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
